<template>
  <div v-if="drug">
    <b-card>
      <b-card-title>Model Information for {{ drug.name }}</b-card-title>
      <b-card-body>
        <b-tabs content-class="mt-3 py-4">
          <b-tab
            v-for="(displayItem, index) in drug.models"
            :key="displayItem.code"
            :title="tabTitle(displayItem)"
            :class="tabClass(displayItem, index)"
            :active="index === selectedIndex"
            @click="selectTab(index)"
          >
            <div class="tab-content overflow-auto">
              <table class="table">
                <tbody>
                  <tr>
                    <th>Drug model</th>
                    <td>{{ displayItem.code }}: {{ displayItem.name }}</td>
                  </tr>
                  <tr>
                    <th>Drug delivery methods supported</th>
                    <td>{{ displayItem.delivery.method }}</td>
                  </tr>
                  <tr>
                    <th>Information</th>
                    <td>
                      <ul v-for="(info, i) in displayItem.info" :key="i">
                        <li>{{ info }}</li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <th>Reference</th>
                    <td>
                      <a :href="displayItem.href">{{ displayItem.href }}</a>
                    </td>
                  </tr>
                  <tr>
                    <th>Population</th>
                    <td>{{ displayItem.population }}</td>
                  </tr>
                  <tr>
                    <th>Age range</th>
                    <td>
                      {{ displayItem.age.range[0] }} - {{ displayItem.age.range[1] }}
                      {{ displayItem.age.unit }}
                    </td>
                  </tr>
                  <tr>
                    <th>Weight range</th>
                    <td>
                      {{ displayItem.weight.range[0] }} - {{ displayItem.weight.range[1] }}
                      {{ displayItem.weight.unit }}
                    </td>
                  </tr>
                  <tr>
                    <th>Coefficients</th>
                    <td>
                      <ul>
                        <li v-for="(cl, i) in displayItem.clearance" :key="i">
                          Clearance ({{ cl.type }}): {{ cl.coeff }} +/- {{ cl.sd }}
                          <span v-html="displaySIUnits(cl.unit)"></span>
                        </li>
                        <li v-for="(cl, i) in displayItem.vd" :key="i">
                          Vd ({{ cl.type }}): {{ cl.coeff }} +/- {{ cl.sd }}
                          <span v-html="displaySIUnits(cl.unit)"></span>
                        </li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <th>Model target ranges</th>
                    <td>
                      <ul v-for="(target, i) in drug.target" :key="i">
                        <li>
                          {{ DrugTargetNames[target.type] }}: {{ target.range[0] }} -
                          {{ target.range[1] }}
                          <span v-html="displaySIUnits(target.unit)"></span> &nbsp;<i
                            >Reference:
                            <a target="_blank" :href="target.href">
                              <i class="bi bi-box-arrow-in-up-right"></i> </a
                          ></i>
                        </li>
                      </ul>
                    </td>
                  </tr>
                  <tr v-if="displayItem.default?.loadingDoseMgPerKg">
                    <th>Dose estimates</th>
                    <td>
                      <ul>
                        <li>Loading dose: {{ displayItem.default.loadingDoseMgPerKg }} mg/kg</li>
                        <li v-if="displayItem.default.maintenanceDoseMgPerKg">
                          Maintenance dose: {{ displayItem.default.maintenanceDoseMgPerKg }} mg/kg
                        </li>
                      </ul>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>
        </b-tabs>
      </b-card-body>
    </b-card>
  </div>
  <div v-else>
    <p>Select a drug model from the dropdown</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { displaySIUnits, DrugTargetNames } from '@/models/utils/constants';
import { useDosingStore } from '@/stores/dosing';

defineOptions({ name: 'ModelInfo' });

const props = defineProps<{ model?: any }>();

const store = useDosingStore();
const selectedIndex = ref(0);

const drug = computed(() => store.getDrug);

watch(
  drug,
  (_newVal) => {
    selectedIndex.value = 0;
  },
  { immediate: true },
);

watch(
  () => props.model,
  (newVal) => {
    if (newVal && drug.value) {
      const newIndex = drug.value.models.indexOf(newVal);
      if (newIndex >= 0) {
        selectedIndex.value = newIndex;
      } else {
        selectedIndex.value = 0;
      }
    } else if (drug.value) {
      selectedIndex.value = 0;
    }
  },
  { immediate: true },
);

function selectTab(idx: number) {
  selectedIndex.value = idx;
}

function tabTitle(displayItem: any) {
  return displayItem.name + (displayItem.code === props.model?.code ? '*' : '');
}

function tabClass(displayItem: any, index: number) {
  return {
    tab: true,
    'tab-bordered': true,
    active: index === selectedIndex.value,
  };
}
</script>

<style scoped>
table th {
  text-align: right;
  width: 20%;
}

table td {
  text-align: left;
}

table td ul li {
  text-align: left;
}
</style>
